import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';

/**
 * Même règle de contraste que le camembert des aléas : texte clair sur fond
 * foncé, texte sombre sur fond clair.
 */
function luminance(hex) {
  const clean = hex.replace('#', '');
  const v = clean.match(/\w\w/g).map((h) => parseInt(h, 16));
  return (0.299 * v[0] + 0.587 * v[1] + 0.114 * v[2]) / 255;
}

function optionPour(data) {
  return {
    textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
    tooltip: {
      trigger: 'item',
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      valueFormatter: (valeur) => `${valeur} %`,
    },
    // Pas de légende ECharts : une seule légende HTML commune est rendue
    // sous l'ensemble des camemberts.
    legend: { show: false },
    series: [
      {
        type: 'pie',
        radius: '92%',
        center: ['50%', '50%'],
        label: {
          show: true,
          position: 'inside',
          fontSize: TAILLE_TEXTE,
          fontWeight: 'bold',
          formatter: ({ percent }) => (percent > 0 ? `${percent} %` : ''),
        },
        labelLine: { show: false },
        data: data.map((c) => ({
          name: c.nom,
          value: c.valeur,
          itemStyle: { color: c.couleur, borderColor: '#ffffff', borderWidth: 2 },
          label: { color: luminance(c.couleur) > 0.6 ? '#111111' : '#ffffff' },
        })),
      },
    ],
  };
}

/**
 * Répartition du risque entre les cultures de l'assolement (Impacts globaux).
 * Chaque culture porte sa propre couleur, fournie par l'API pour rester
 * cohérente avec les camemberts d'assolement.
 *
 * Deux usages :
 *  - `annees` : un camembert par horizon, côte à côte, avec son libellé en
 *    dessous et UNE seule légende commune centrée sous l'ensemble ;
 *  - `data`   : un seul camembert (ancien usage), sans libellé d'horizon.
 *
 * @param annees [{ cle, label, data: [{ nom, valeur, couleur }] }]
 * @param data   [{ nom, valeur, couleur }]
 */
export default function RepartitionCulturesChart({ annees, data = [] }) {
  const groupes = (annees ?? [{ cle: 'unique', label: null, data }]).filter(
    (g) => g.data?.length > 0
  );

  if (groupes.length === 0) {
    return <p className="tdb-chargement">Répartition indisponible.</p>;
  }

  // Légende commune : union des cultures de tous les horizons, dans l'ordre
  // de première apparition (une culture peut manquer sur un horizon).
  const legende = new Map();
  groupes.forEach((g) =>
    g.data.forEach((c) => {
      if (!legende.has(c.nom)) legende.set(c.nom, c.couleur);
    })
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%' }}>
        {groupes.map((g) => (
          <div key={g.cle} style={{ flex: '1 1 200px', minWidth: 180, textAlign: 'center' }}>
            <ResponsiveECharts option={optionPour(g.data)} style={{ height: g.label ? 300 : 380 }} />
            {g.label && <div style={{ fontWeight: 'bold', marginTop: 4 }}>{g.label}</div>}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          width: '100%',
          justifyContent: 'center',
        }}
      >
        {Array.from(legende, ([nom, couleur]) => (
          <div key={nom} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                background: couleur,
                display: 'inline-block',
                borderRadius: 2,
              }}
            />
            <span style={{ fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE }}>{nom}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
